import { makeStyles } from "tss-react/mui"
import { Box, List, ListItem, Stack, Typography } from "@mui/material"
import { faUser } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { AccordionSection } from "./AccordionSection"
import { AttendeeDetails } from "../../createUpdateBookingDialog/attendees/attendeesForm/attendeesTypes"
import { useMemo } from "react"
import { tailwindPalette } from "../../../../../modules/theme/tailwindPalette"

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: tailwindPalette.slate[400],
        marginTop: ".25rem",
    },
    accordion: {
        background: tailwindPalette.emerald[50],
        [theme.breakpoints.down("sm")]: {
            background: "white",
            borderRadius: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            "&  .MuiAccordionSummary-root": {
                padding: "0 1.25rem 2rem 1.25rem",
            },
        },
        "@media print": {
            height: "fit-content",
        },
    },
    textColor: {
        color: theme.palette.text.primary,
    },
    container: {
        display: "grid",
        gap: "1rem",
        gridTemplateColumns: "1fr 1fr",
    },
    list: {
        display: "grid",
        gap: ".5rem",
        listStyleType: "disc",
        padding: "0",
        paddingLeft: "1rem",
        "@media print": {
            display: "none",
        },
    },
    listItem: {
        display: "list-item",
        "&::marker": {
            color: tailwindPalette.slate[400],
        },
    },
    subtitleContainer: {
        "@media print": {
            position: "absolute",
            top: "1rem",
            right: "1.5rem",
        },
    },
    accordionDetails: {
        "@media print": {
            display: "none",
        },
    },
}))

type AttendeesProps = Readonly<{
    list: AttendeeDetails[]
}>

export function Attendees({ list }: AttendeesProps) {
    const { classes, cx } = useStyles()

    const { internalAttendees, externalAttendees } = useMemo(() => {
        const internal: AttendeeDetails[] = []
        const external: AttendeeDetails[] = []

        for (const attendee of list) {
            if (attendee.isExternal) {
                external.push(attendee)
            } else {
                internal.push(attendee)
            }
        }

        return { internalAttendees: internal, externalAttendees: external }
    }, [list])

    const internalCount = internalAttendees.length
    const externalCount = externalAttendees.length

    const formatName = (a: AttendeeDetails) => `${a.name}, ${a.company}`

    return (
        <LocalizedStrict id="confirm-order-dialog-attendees" attrs={{ title: true }}>
            <AccordionSection
                title="Participants"
                subTitle={
                    <Stack flexDirection="row" gap=".5rem" className={classes.subtitleContainer}>
                        <FontAwesomeIcon icon={faUser} className={classes.icon} />
                        <LocalizedStrict
                            id="confirm-order-dialog-attendees-subtitle"
                            vars={{
                                internalCount: internalCount,
                                externalCount: externalCount,
                            }}
                        >
                            <Typography variant="body1" className={classes.textColor}>
                                {internalCount} internal, {externalCount} external
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                }
                updateAccordionUi={classes.accordion}
                updateAccordionDetails={classes.accordionDetails}
            >
                <Box className={classes.container}>
                    {!!internalCount && (
                        <Stack gap=".75rem">
                            <LocalizedStrict
                                id="confirm-order-dialog-attendees-internal"
                                vars={{ internalCount: internalCount }}
                            >
                                <Typography variant="subtitle1">Internal attendees ({internalCount})</Typography>
                            </LocalizedStrict>
                            <List dense className={classes.list}>
                                {internalAttendees.map((person, idx) => (
                                    <ListItem key={idx} disablePadding className={classes.listItem}>
                                        <Typography variant="body1">{formatName(person)}</Typography>
                                    </ListItem>
                                ))}
                            </List>
                        </Stack>
                    )}
                    {!!externalCount && (
                        <Stack gap=".75rem">
                            <LocalizedStrict
                                id="confirm-order-dialog-attendees-external"
                                vars={{ externalCount: externalCount }}
                            >
                                <Typography variant="subtitle1">External attendees ({externalCount})</Typography>
                            </LocalizedStrict>
                            <List dense className={classes.list}>
                                {externalAttendees.map((person, idx) => (
                                    <ListItem key={idx} disablePadding className={classes.listItem}>
                                        <Typography variant="body1">{formatName(person)}</Typography>
                                    </ListItem>
                                ))}
                            </List>
                        </Stack>
                    )}
                </Box>
            </AccordionSection>
        </LocalizedStrict>
    )
}
